import axios from "axios";
import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {RecipientOptOutService} from "../gen/kitecorp/transferify/email/v1/recipient_opt_out_pb";

/**
 * Client for the anonymous recipient opt-out (kitecorp/transferify-api-service#222): the status read is
 * `RecipientOptOutService.GetRecipientOptOutStatus` on the Connect transport (kitecorp/transferify-ui#3944), the write
 * a plain HTTP POST.
 *
 * Recipients of a transfer have no account — their address was supplied by a sender — so the signed
 * token from the email link is the only credential these calls carry, and an absent session is expected
 * rather than an error.
 */

const OPT_OUT_PATH = "/email/opt-out";

/** What a token refers to, as reported by the api. */
export interface OptOutStatus {
	email: string;
	unsubscribed: boolean;
}

/**
 * Resolves what a token names WITHOUT unsubscribing anyone — the api deliberately keeps the read
 * separate from the write so a mail scanner following the link cannot opt somebody out.
 *
 * @throws a `ConnectError` for a token that is missing, malformed or not authentic, and when the module behind the
 *         read is away; `responseStatus` reads the HTTP status it came back with. From
 *         kitecorp/transferify-api-service#3159 on, the api's adapter answers the first as `invalid_argument` (400)
 *         and the second as `unavailable` (503).
 */
export async function fetchOptOutStatus(token: string): Promise<OptOutStatus> {
	const {email, unsubscribed} = await createClient(RecipientOptOutService, apiTransport)
		.getRecipientOptOutStatus({linkToken: token});
	return {email, unsubscribed};
}

/**
 * Records the opt-out. Idempotent server-side, so re-submitting an already-used link succeeds instead
 * of reporting an error the recipient can do nothing about.
 *
 * Stays a plain HTTP POST: this route is the RFC 8058 one-click unsubscribe that mailbox providers post as a form,
 * which the epic keeps outside Connect (kitecorp/transferify#418, "What stays plain HTTP").
 */
export async function submitOptOut(token: string): Promise<OptOutStatus> {
	const {data} = await axios.post<OptOutStatus>(OPT_OUT_PATH, null, {params: {token}});
	return data;
}
